import * as i0 from '@angular/core';
import { Injectable, Inject, NgZone, Injector, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { set, get } from 'lodash-es';
import { Observable } from 'rxjs';
import { TestBed } from '@angular/core/testing';

class CookieService {
    constructor(document) {
        this.document = document;
    }
    getCookie(cname) {
        const name = cname + '=';
        const ca = this.document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    }
    setCookie(cname, cvalue, exdays) {
        const d = new Date();
        d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + d.toUTCString();
        this.document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
    }
}
CookieService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CookieService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/**
 * Return the global native browser window object
 */
function _window() {
    return window;
}
/**
 * Export a reference of the global native browser window object.
 */
class WindowRef {
    get nativeWindow() {
        return _window();
    }
}
WindowRef.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
WindowRef.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowRef });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable
        }] });

/**
 * @service
 * @description
 * The NativeBridgeService provides allows Angular applications
 * to send and receive messages over the JavaScript Native Bridge.
 */
class NativeBridgeService {
    constructor(_windowRef, _zone) {
        this._windowRef = _windowRef;
        this._zone = _zone;
        this._device = {
            embedded: null,
            platform: {
                ios: null,
                android: null
            }
        };
        this._nativeBridge = this._windowRef.nativeWindow.disney.nativeBridgeFactory.createNativeBridge();
        this._device = this._windowRef.nativeWindow.disney.device;
    }
    /**
     * @description Sends a custom message to the native app.
     *
     * @param commandName: The identifier for the message
     * @param params: A JSON object of keys and values
     * @param sendFailedCallback: A closure that will be invoked if he message fails to send.
       *	It will have 3 parameters: commandName, params, error
     */
    sendCustomMessage(commandName, params, sendFailedCallback) {
        this._nativeBridge.sendCustomMessage(commandName, params, sendFailedCallback);
    }
    /**
     * @description Sends a ready message to the native app.
       *  This message is used to inform the native app that the web page is ready to receive commands.
     *
     * @param params: A JSON object of keys and values
     * @param sendFailedCallback: A closure that will be invoked if he message fails to send.
       *	It will have 3 parameters: commandName, params, error
     */
    sendReadyMessage(params, sendFailedCallback) {
        this._nativeBridge.sendReadyMessage(params, sendFailedCallback);
    }
    /**
     * @description Sends an error message to the native app.
       *  This message is used to inform the native app that an error occurred on the web page.
     *
     * @param params: A JSON object of keys and values
     * @param sendFailedCallback: A closure that will be invoked if he message fails to send.
       *	It will have 3 parameters: commandName, params, error
     */
    sendErrorMessage(params, sendFailedCallback) {
        this._nativeBridge.sendErrorMessage(params, sendFailedCallback);
    }
    /**
     * @description Sends a close message to the native app.
       *  This message is used to inform the native app that the web page should be closed.
     *
     * @param commandName: The identifier for the message
     * @param params: A JSON object of keys and values
     * @param sendFailedCallback: A closure that will be invoked if he message fails to send.
       *	It will have 3 parameters: commandName, params, error
     */
    sendCloseMessage(params, sendFailedCallback) {
        this._nativeBridge.sendCloseMessage(params, sendFailedCallback);
    }
    /**
     * @description Exposes a JavaScript function which the native application can invoke.
     *
     * @param path: The path off of the window object where this function can be accessed.
     * @param functionBody: The function which will be invoked by the native app.
     */
    exposeFunction(path, functionBody) {
        set(this._windowRef.nativeWindow, path, wrapperFunction.bind(this));
        function wrapperFunction( /*arguments from native call*/) {
            const args = arguments;
            this._zone.run(() => {
                functionBody(args);
            });
        }
    }
    /**
     * @description Returns an Observable which fires each time the native application send a message to the specified path.
     *
     * @param path: The path off of the window object to which the native app will send messages.
     */
    observeFunction(path) {
        return new Observable((observer) => {
            // For some reason, binding this into the wrapper results in the parents' scope being bound to the function.
            // This is necessary to bind the current scope (the Observable) instead of the parents' scope.
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;
            set(this._windowRef.nativeWindow, path, wrappedFunction.bind(that));
            function wrappedFunction(...args) {
                this._zone.run(() => {
                    observer.next(args);
                });
            }
            return {
                unsubscribe() { }
            };
        });
    }
    /**
     * @description Returns device info for the current device state.
     *
     * @returns DeviceInfo
     */
    getDeviceInfo() {
        return this._device;
    }
    /**
     * @description Returns an object containing configuration data for the current environment
     */
    loadEnvironmentData() {
        return this._windowRef.nativeWindow.disney.loadEnvironmentData();
    }
}
NativeBridgeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeService, deps: [{ token: WindowRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
NativeBridgeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: WindowRef }, { type: i0.NgZone }]; } });

class NativeBridgeServiceMock extends NativeBridgeService {
    constructor(windowRef, zone, injector) {
        super(windowRef, zone);
        this.windowRef = windowRef;
        this.zone = zone;
        this.injector = injector;
        this.TAG = 'NativeBridgeServiceMock';
        this.READY_COMMAND_NAME = 'disneyRAWebViewBridgeReady';
        this.ERROR_COMMAND_NAME = 'disneyRAWebViewBridgeError';
        this.CLOSE_COMMAND_NAME = 'disneyRAWebViewBridgeClose';
        console.log(`${this.TAG}: Native Bridge Mock is enabled`);
        this.nativeBridgeMockData = TestBed.inject(null);
        Object.keys(this.nativeBridgeMockData).forEach(commandName => {
            set(this.windowRef.nativeWindow, commandName, () => { });
        });
    }
    sendCustomMessage(commandName, params, sendFailedCallback) {
        const mockValues = this.nativeBridgeMockData[commandName];
        if (!mockValues) {
            console.error(`${this.TAG}: The command ${commandName} does not have any mock configuration.`);
            return;
        }
        const webExposedFuncName = mockValues.webExposedFuncName;
        console.log(`${this.TAG}: Native received call to ${commandName}.`);
        if (!webExposedFuncName) {
            console.log(`${this.TAG}: The command ${commandName} has no configured web callback.`);
            return;
        }
        console.log(this.TAG, this.windowRef.nativeWindow);
        const webExposedFunc = get(this.windowRef.nativeWindow, webExposedFuncName);
        if (webExposedFuncName && !webExposedFunc) {
            console.error(`${this.TAG}: Calling function ${webExposedFuncName} from native failed. Are you sure it was exposed?`);
            return;
        }
        webExposedFunc(mockValues.params);
    }
    sendReadyMessage(params, sendFailedCallback) {
        this.sendCustomMessage(this.READY_COMMAND_NAME, params, sendFailedCallback);
    }
    sendErrorMessage(params, sendFailedCallback) {
        this.sendCustomMessage(this.ERROR_COMMAND_NAME, params, sendFailedCallback);
    }
    sendCloseMessage(params, sendFailedCallback) {
        this.sendCustomMessage(this.CLOSE_COMMAND_NAME, params, sendFailedCallback);
    }
}

function NativeBridgeLoader(cookieService, windowRef, zone, injector) {
    return cookieService.getCookie('enable_native_bridge_mock') ?
        new NativeBridgeServiceMock(windowRef, zone, injector) :
        new NativeBridgeService(windowRef, zone);
}

let NativeBridgeProvider = {
    provide: NativeBridgeService,
    useFactory: NativeBridgeLoader,
    deps: [
        CookieService,
        WindowRef,
        NgZone,
        Injector
    ]
};

/**
 * @module
 * @description
 * The NativeBridgeModule provides services to communicate with
 * native applications using the RA Hybrid framework.
 * To get started, see the {@link NativeBridgeService} class.
 */
class NativeBridgeModule {
}
NativeBridgeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NativeBridgeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeModule });
NativeBridgeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeModule, providers: [
        CookieService,
        NativeBridgeService,
        WindowRef
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: NativeBridgeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: [
                        CookieService,
                        NativeBridgeService,
                        WindowRef
                    ]
                }]
        }] });

/*
 * Public API Surface of ra-angular-native-bridge
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NativeBridgeModule, NativeBridgeProvider, NativeBridgeService };
